'use client';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, Circle, isServer, Stack, XStack, YStack } from 'tamagui';

type ImageCarouselProps = {
  images: any[];
  interval?: number;
  onImagePress?: (item: any, index: number) => void;
  autoPlayInterval?: number;
  dotSize?: number;
  containerStyle?: any;
};

export function CustomizedCarousel({ images, onImagePress, interval = 3000 }: ImageCarouselProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  // const media = useMedia();

  useEffect(() => {
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated || images.length === 0 || paused) return;

    const timer = setInterval(() => {
      const next = (activeIndex + 1) % images.length;
      scrollToIndex(next);
    }, interval);

    return () => clearInterval(timer);
  }, [activeIndex, hydrated, paused, images.length, interval]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onScroll = () => {
      const scrollLeft = container.scrollLeft;
      const width = container.offsetWidth;
      const newIndex = Math.round(scrollLeft / width);
      setActiveIndex(newIndex);
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToIndex = (index: number) => {
    const container = containerRef.current;
    if (!container) return;

    const child = container.children[index] as HTMLElement;
    if (child) {
      child.scrollIntoView({ behavior: 'smooth', inline: 'start' });
      setActiveIndex(index);
    }
  };

  // const imageHeight = media.sm ? 160 : media.md ? 200 : 220;

  return (
    <YStack
      width="100%"
      alignItems="center"
      space="$2"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <XStack
        ref={containerRef}
        overflow="scroll"
        width="100%"
        maxWidth={600}
        gap="$1"
        px="$1"
        style={{
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {images.map((src, index) => (
          <YStack
            key={index}
            minWidth="100%"
            height={200}
            minHeight={200}
            flexShrink={0}
            position="relative"
            borderRadius="$4"
            overflow="hidden"
            style={{
              scrollSnapAlign: 'start',
            }}
            paddingHorizontal={8}
          >
            <AnimatePresence>
              {index === activeIndex && (
                <Stack
                  animation="quick"
                  enterStyle={{ opacity: 1 }}
                  exitStyle={{ opacity: 0.5 }}
                  opacity={1}
                  width="100%"
                  height="100%"
                  onPress={() => onImagePress?.(src, index)}
                  aspectRatio={3 / 2}
                >
                  <Image
                    src={src?.url}
                    alt={`Image ${index + 1}`}
                    fill
                    sizes="(max-width: 600px) 100vw, 600px"
                    style={{
                      objectFit: 'contain',
                      width: '100%',
                      height: '100%',
                      maxWidth: '600px',
                    }}
                    priority={index === 0}
                  />
                </Stack>
              )}
            </AnimatePresence>
          </YStack>
        ))}
      </XStack>

      {/* Pagination */}
      {images.length > 1 && (
        <XStack gap="$2" justifyContent="center">
          {images.map((_, i) => (
            <Circle
              key={i}
              marginBottom={4}
              size={'$0.75'}
              backgroundColor={i === activeIndex ? '$primary' : '$gray'}
              onPress={() => scrollToIndex(i)}
              cursor="pointer"
            />
          ))}
        </XStack>
      )}
    </YStack>
  );
}
